import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { Button, Input, Label, toast } from '@employer-on-record/ui';
import type { UserDTO } from '@employer-on-record/shared';
import { adminApi } from '@/services/api';

type Mode = 'create' | 'edit';
type UserKind = 'hr' | 'approver';

interface Props {
	open: boolean;
	mode: Mode;
	kind: UserKind;
	companyId: string;
	companyName: string;
	user: (UserDTO & { department?: string | null; position?: string | null }) | null;
	onClose: () => void;
}

const blank = { name: '', email: '', department: '', position: '', password: '' };

export function UserFormDrawer({ open, mode, kind, companyId, companyName, user, onClose }: Props) {
	const qc = useQueryClient();
	const [form, setForm] = useState(blank);

	useEffect(() => {
		if (mode === 'edit' && user) {
			setForm({
				name: user.name,
				email: user.email,
				department: user.department ?? '',
				position: user.position ?? '',
				password: ''
			});
		} else if (open) {
			setForm(blank);
		}
	}, [mode, user, open]);

	const submit = useMutation({
		mutationFn: async () => {
			if (mode === 'edit' && user) {
				await adminApi.updateUser(user.id, {
					name: form.name,
					email: form.email,
					department: form.department || null,
					position: form.position || null
				});
				return;
			}
			const body = {
				companyId,
				name: form.name,
				email: form.email,
				department: form.department || undefined,
				position: form.position || undefined,
				password: form.password || undefined
			};
			if (kind === 'hr') await adminApi.createHrUser(body);
			else await adminApi.createApproverUser(body);
		},
		onSuccess: () => {
			toast(mode === 'create' ? `${kind === 'hr' ? 'HR user' : 'Approver'} created` : 'User updated');
			qc.invalidateQueries({ queryKey: ['admin-company-hr', companyId] });
			qc.invalidateQueries({ queryKey: ['admin-company-approvers', companyId] });
			qc.invalidateQueries({ queryKey: ['admin-hr-users'] });
			onClose();
		},
		onError: (err) => toast((err as Error).message, 'destructive')
	});

	if (!open) return null;
	const noun = kind === 'hr' ? 'HR user' : 'Approver';

	return (
		<div className="fixed inset-0 z-40 flex">
			<div className="flex-1 bg-black/30" onClick={onClose} />
			<div className="w-[420px] bg-card border-l overflow-y-auto">
				<div className="px-5 py-4 border-b flex items-center">
					<h3 className="font-semibold">
						{mode === 'create' ? `Add ${noun}` : `Edit ${noun}`} · {companyName}
					</h3>
					<button onClick={onClose} className="ml-auto p-1 rounded hover:bg-background"><X className="h-4 w-4" /></button>
				</div>
				<form onSubmit={(e) => { e.preventDefault(); submit.mutate(); }} className="p-5 grid gap-3">
					<div className="grid gap-1.5">
						<Label htmlFor="u-name">Full name</Label>
						<Input id="u-name" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} required />
					</div>
					<div className="grid gap-1.5">
						<Label htmlFor="u-email">Email</Label>
						<Input id="u-email" type="email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} required />
					</div>
					<div className="grid grid-cols-2 gap-3">
						<div className="grid gap-1.5">
							<Label htmlFor="u-dept">Department</Label>
							<Input id="u-dept" value={form.department} onChange={(e) => setForm(f => ({ ...f, department: e.target.value }))} />
						</div>
						<div className="grid gap-1.5">
							<Label htmlFor="u-pos">Position</Label>
							<Input id="u-pos" value={form.position} onChange={(e) => setForm(f => ({ ...f, position: e.target.value }))} />
						</div>
					</div>
					{mode === 'create' && (
						<div className="grid gap-1.5">
							<Label htmlFor="u-pass">Initial password (leave blank for default)</Label>
							<Input id="u-pass" type="text" value={form.password} onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))} placeholder="changeme1234" />
						</div>
					)}
					<div className="flex gap-2 mt-4">
						<Button type="button" variant="secondary" onClick={onClose} className="flex-1 justify-center">Cancel</Button>
						<Button type="submit" className="flex-1 justify-center" disabled={submit.isPending}>
							{submit.isPending ? 'Saving…' : mode === 'create' ? `Create ${noun.toLowerCase()}` : 'Save'}
						</Button>
					</div>
				</form>
			</div>
		</div>
	);
}
